/**
 * Public Chat Container
 * 
 * Main chat interface for the public landing page.
 */

import React, { useRef, useEffect, useState } from 'react';
import { Box, TextField, IconButton, CircularProgress, Alert, Snackbar } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { usePublicChat } from '../hooks/use-public-chat';
import { PublicWelcome } from './PublicWelcome';
import { SignupCard } from '../cards/SignupCard';
import { LoginCard } from '../cards/LoginCard';
import { PricingCard } from '../cards/PricingCard';
import { FeaturesCard } from '../cards/FeaturesCard';
import { SafetyCard } from '../cards/SafetyCard';
import type { PublicMessage, QuickAction, CardType } from '../public-chat.types';

interface PublicChatContainerProps {
  showAuthCard: 'signup' | 'login' | null;
  onShowAuthCard: (type: 'signup' | 'login' | null) => void;
}

export const PublicChatContainer: React.FC<PublicChatContainerProps> = ({
  showAuthCard,
  onShowAuthCard,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');

  const {
    sessionId,
    messages,
    isLoading,
    isSending,
    error,
    quickActions,
    sendMessage,
    clearError,
  } = usePublicChat();

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle send
  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;
    const message = inputValue;
    setInputValue('');
    await sendMessage(message);
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle quick action
  const handleQuickAction = (action: QuickAction) => {
    sendMessage(action.label);
  };

  // Render card based on type
  const renderCard = (cardType: CardType) => {
    switch (cardType) {
      case 'SHOW_PRICING':
        return <PricingCard key={cardType} onSignup={() => onShowAuthCard('signup')} />;
      case 'SHOW_SIGNUP':
        return <SignupCard key={cardType} sessionId={sessionId || ''} onSwitchToLogin={() => onShowAuthCard('login')} />;
      case 'SHOW_LOGIN':
        return <LoginCard key={cardType} sessionId={sessionId || ''} onSwitchToSignup={() => onShowAuthCard('signup')} />;
      case 'SHOW_FEATURES':
        return <FeaturesCard key={cardType} />;
      case 'SHOW_SAFETY':
        return <SafetyCard key={cardType} />;
      default:
        return null;
    }
  };

  // Render message
  const renderMessage = (message: PublicMessage) => {
    const isUser = message.role === 'user';
    const cards = message.metadata?.cards || [];
    const isStreaming = message.isStreaming;

    return (
      <Box
        key={message.id}
        className={`${classes.messageWrapper} ${isUser ? classes.userWrapper : classes.assistantWrapper}`}
      >
        <Box className={`${classes.message} ${isUser ? classes.userMessage : classes.assistantMessage}`}>
          {isUser ? (
            <span>{message.content}</span>
          ) : (
            <>
              <ReactMarkdown className={classes.markdown}>
                {message.content}
              </ReactMarkdown>
              {isStreaming && (
                <span className={classes.streamingCursor}>▊</span>
              )}
            </>
          )}
        </Box>

        {/* Render embedded cards */}
        {cards.length > 0 && (
          <Box className={classes.cardsContainer}>
            {cards.map((cardType) => renderCard(cardType as CardType))}
          </Box>
        )}
      </Box>
    );
  };

  // Show welcome if no user messages yet
  const hasUserMessages = messages.some((m) => m.role === 'user');

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={40} color="primary" />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Auth Card Overlay */}
      {showAuthCard && (
        <Box className={classes.authOverlay}>
          <Box className={cx(classes.authCard, showAuthCard === 'signup' && classes.authCardWide)}>
            {showAuthCard === 'signup' ? (
              <SignupCard
                sessionId={sessionId || ''}
                onSwitchToLogin={() => onShowAuthCard('login')}
                onClose={() => onShowAuthCard(null)}
              />
            ) : (
              <LoginCard
                sessionId={sessionId || ''}
                onSwitchToSignup={() => onShowAuthCard('signup')}
                onClose={() => onShowAuthCard(null)}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Messages Area */}
      <Box className={classes.messagesArea}>
        {!hasUserMessages ? (
          <PublicWelcome quickActions={quickActions} onQuickAction={handleQuickAction} />
        ) : (
          <Box className={classes.messages}>
            {messages.map((msg) => renderMessage(msg))}

            {/* Typing indicator */}
            {isSending && (
              <Box className={`${classes.messageWrapper} ${classes.assistantWrapper}`}>
                <Box className={`${classes.message} ${classes.assistantMessage} ${classes.typing}`}>
                  <span className={classes.typingDot} />
                  <span className={classes.typingDot} />
                  <span className={classes.typingDot} />
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Input Area */}
      <Box className={classes.inputArea}>
        <Box className={classes.inputContainer}>
          <TextField
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('publicChat.inputPlaceholder', 'Ask me anything about SAIA...')}
            variant="outlined"
            fullWidth
            multiline
            maxRows={4}
            disabled={isSending}
            className={classes.input}
            InputProps={{
              className: classes.inputField,
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!inputValue.trim() || isSending}
            className={classes.sendButton}
          >
            {isSending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Box>
        <Box className={classes.inputHint}>
          <span>Press Enter to send</span>
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clearError} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  authOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: theme.spacing(2),
  },
  authCard: {
    maxWidth: 420,
    width: '100%',
  },
  // Registrierung hat mehr Felder (Name, Firma, Passwort ×2, Rechtliches) — breiter als Login
  authCardWide: {
    maxWidth: 560,
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  messages: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: 800,
    margin: '0 auto',
    width: '100%',
  },
  messageWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  userWrapper: {
    alignItems: 'flex-end',
  },
  assistantWrapper: {
    alignItems: 'flex-start',
  },
  message: {
    maxWidth: '80%',
    padding: theme.spacing(1.5, 2),
    borderRadius: 16,
    fontSize: '0.95rem',
    lineHeight: 1.5,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '90%',
    },
  },
  userMessage: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottomRightRadius: 4,
  },
  assistantMessage: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottomLeftRadius: 4,
    border: `1px solid ${theme.palette.greys.lighter}`,
  },
  markdown: {
    '& p': {
      margin: 0,
      '&:not(:last-child)': {
        marginBottom: theme.spacing(1),
      },
    },
    '& ul, & ol': {
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2.5),
    },
    '& code': {
      backgroundColor: theme.palette.greys.lightest,
      padding: '2px 6px',
      borderRadius: 4,
      fontSize: '0.85em',
      color: theme.palette.primary.dark,
    },
    '& pre': {
      backgroundColor: theme.palette.greys.lightest,
      padding: theme.spacing(1.5),
      borderRadius: 8,
      overflow: 'auto',
      border: `1px solid ${theme.palette.greys.lighter}`,
    },
    '& a': {
      color: theme.palette.primary.main,
    },
    '& strong': {
      fontWeight: 600,
    },
  },
  cardsContainer: {
    marginTop: theme.spacing(1.5),
    maxWidth: '100%',
  },
  typing: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: theme.spacing(1.5, 2),
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.light,
    animation: 'typingPulse 1.4s infinite',
    '&:nth-of-type(2)': {
      animationDelay: '0.2s',
    },
    '&:nth-of-type(3)': {
      animationDelay: '0.4s',
    },
    '@keyframes typingPulse': {
      '0%, 60%, 100%': {
        opacity: 0.4,
        transform: 'scale(1)',
      },
      '30%': {
        opacity: 1,
        transform: 'scale(1.2)',
      },
    },
  },
  inputArea: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: theme.palette.background.paper,
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    maxWidth: 800,
    margin: '0 auto',
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.greys.lightest,
      borderRadius: 12,
      '& fieldset': {
        borderColor: theme.palette.greys.light,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.greys.medium,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  inputField: {
    color: theme.palette.text.primary,
    fontSize: '0.95rem',
    '&::placeholder': {
      color: theme.palette.text.secondary,
    },
  },
  sendButton: {
    width: 48,
    height: 48,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 12,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.greys.lighter,
      color: theme.palette.greys.medium,
    },
  },
  inputHint: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
    '& span': {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
    },
  },
  streamingCursor: {
    display: 'inline-block',
    color: theme.palette.primary.main,
    animation: 'blink 1s step-end infinite',
    marginLeft: '2px',
    '@keyframes blink': {
      '0%, 50%': { opacity: 1 },
      '51%, 100%': { opacity: 0 },
    },
  },
}));

export default PublicChatContainer;

